import { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { CloudConnectionEndpoints } from "@generated/cloud-connections";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

interface ArmTemplateResult {
  templateJson: string;
  deployInstructions: string;
}

interface ValidationResult {
  success: boolean;
  message: string;
}

interface Props {
  organizationAlias: string;
}

export function AzureSetupPanel({ organizationAlias }: Props) {
  const [subscriptionId, setSubscriptionId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [connectionName, setConnectionName] = useState("Azure");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ArmTemplateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidationResult | null>(
    null,
  );

  const generate = async () => {
    if (!subscriptionId || !tenantId) return;
    setLoading(true);
    setResult(null);
    setError(null);
    setValidateResult(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${CloudConnectionEndpoints.AZURE_ARM_TEMPLATE}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            organizationAlias,
            azureSubscriptionId: subscriptionId,
            azureTenantId: tenantId,
          }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setResult(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate template");
    } finally {
      setLoading(false);
    }
  };

  const validate = async () => {
    if (!subscriptionId || !tenantId) return;
    setValidating(true);
    setValidateResult(null);
    setError(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${CloudConnectionEndpoints.AZURE_VALIDATE}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            organizationAlias,
            name: connectionName || "Azure",
            azureSubscriptionId: subscriptionId,
            azureTenantId: tenantId,
          }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setValidateResult(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const downloadTemplate = () => {
    if (!result) return;
    const blob = new Blob([result.templateJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rusha-lighthouse.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Azure Lighthouse
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate an Azure Lighthouse ARM template that grants Rusha read access
        to your subscription for deployment orchestration, then validate the
        connection.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
        <TextField
          label="Connection name"
          placeholder="Production Azure"
          value={connectionName}
          onChange={(e) => setConnectionName(e.target.value)}
          size="small"
        />
        <TextField
          label="Azure Subscription ID"
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          value={subscriptionId}
          onChange={(e) => setSubscriptionId(e.target.value)}
          size="small"
        />
        <TextField
          label="Azure Tenant ID"
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          size="small"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          onClick={generate}
          disabled={loading || !subscriptionId || !tenantId}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          Generate ARM template
        </Button>
        <Button
          variant="contained"
          onClick={validate}
          disabled={validating || !subscriptionId || !tenantId}
          startIcon={
            validating ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Verify connection
        </Button>
      </Box>

      {result && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
            >
              {result.deployInstructions}
            </Typography>
          </Alert>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              ARM Template JSON
            </Typography>
            <Box>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(result.templateJson)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton size="small" onClick={downloadTemplate}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box
            component="pre"
            sx={{
              p: 1.5,
              bgcolor: "grey.900",
              color: "grey.100",
              borderRadius: 1,
              fontSize: "0.75rem",
              overflow: "auto",
              maxHeight: 300,
            }}
          >
            {result.templateJson}
          </Box>
        </Box>
      )}

      {validateResult && (
        <Alert
          severity={validateResult.success ? "success" : "error"}
          icon={validateResult.success ? <CheckCircleIcon /> : undefined}
          sx={{ mt: 2 }}
        >
          {validateResult.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
